import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Modal, Empty } from "../components/ui-bits";
import { formatCurrency } from "../lib/mmsPricing";
import { useAuth } from "../lib/auth";
import { Loader2, Send, Check, XCircle, Eye, Image as ImageIcon, RefreshCcw } from "lucide-react";

/* Simple lookup so status pills stay consistent with backend states. */
const STATUS_META = {
  draft:          { label: "Draft",           tone: "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]" },
  quote_requested:{ label: "Quote requested", tone: "bg-amber-50 text-amber-800 border-amber-300" },
  in_review:      { label: "In review",       tone: "bg-blue-50 text-blue-800 border-blue-300" },
  converted:      { label: "Converted",       tone: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  rejected:       { label: "Rejected",        tone: "bg-red-50 text-red-700 border-red-300" },
};

const FILTERS = [
  { key: "pending", label: "Pending", statuses: ["quote_requested", "in_review"] },
  { key: "all",     label: "All",     statuses: null },
  { key: "quote_requested", label: "Requested", statuses: ["quote_requested"] },
  { key: "in_review",       label: "In review", statuses: ["in_review"] },
  { key: "converted",       label: "Converted", statuses: ["converted"] },
  { key: "rejected",        label: "Rejected",  statuses: ["rejected"] },
];

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", tone: "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest border ${meta.tone}`}>
      {meta.label}
    </span>
  );
}

function totalDisplay(estimate, currency) {
  if (!estimate) return formatCurrency(0, currency);
  const t = estimate.total;
  if (t && typeof t === "object" && t.min != null && t.max != null) {
    return t.min === t.max ? formatCurrency(t.min, currency) : `${formatCurrency(t.min, currency)} – ${formatCurrency(t.max, currency)}`;
  }
  return formatCurrency(t || 0, currency);
}

/** AI engine "max" — the internal upper bound produced by the pricing engine
 *  before any admin override. Used to compute the AI→MMS delta. */
function aiEngineTotal(estimate) {
  if (!estimate) return 0;
  const t = estimate.total;
  if (t && typeof t === "object") return Number(t.max ?? t.min ?? 0) || 0;
  return Number(t || 0) || 0;
}

function Thumb({ sim, size = 40 }) {
  const b64 = sim?.inputs?.ai_analysis?.image_thumbnail_b64;
  if (!b64) {
    return (
      <div className="flex items-center justify-center bg-[#FAFAFA] border border-[#E5E5E5] text-[#B3B3B3]"
           style={{ width: size, height: size }}>
        <ImageIcon size={size / 2.5} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      src={`data:image/jpeg;base64,${b64}`}
      alt="reference"
      className="object-cover border border-[#E5E5E5]"
      style={{ width: size, height: size }}
    />
  );
}

export default function MmsQuoteDashboardPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);   // full simulation for modal
  const [savingStatus, setSavingStatus] = useState(false);
  const [adminNote, setAdminNote] = useState("");
  const [overrideDraft, setOverrideDraft] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/mms/admin/simulations");
      setRows(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    if (!f?.statuses) return rows;
    const set = new Set(f.statuses);
    return rows.filter((r) => set.has(r.status));
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { total: rows.length };
    for (const key of Object.keys(STATUS_META)) c[key] = 0;
    for (const r of rows) if (c[r.status] !== undefined) c[r.status] += 1;
    return c;
  }, [rows]);

  if (user && !user.is_super_admin) return <Navigate to="/dashboard" replace />;

  const openDetail = async (sim) => {
    setAdminNote(sim.admin_note || "");
    try {
      const { data } = await api.get(`/mms/admin/simulations/${sim.id}`);
      setDetail(data);
      setOverrideDraft(data.admin_final_price != null ? String(data.admin_final_price) : "");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const changeStatus = async (status) => {
    if (!detail) return;
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/mms/admin/simulations/${detail.id}/status`, {
        status, admin_note: adminNote || null,
      });
      setDetail((d) => ({ ...d, ...data }));
      setRows((rs) => rs.map((r) => (r.id === data.id ? { ...r, ...data } : r)));
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingStatus(false); }
  };

  const saveOverride = async () => {
    if (!detail) return;
    const price = Number(overrideDraft);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Insert a valid final price");
      return;
    }
    setSavingOverride(true);
    try {
      const { data } = await api.post(`/mms/admin/simulations/${detail.id}/override-price`, {
        final_price: price, admin_note: adminNote || null,
      });
      setDetail((d) => ({ ...d, ...data }));
      setRows((rs) => rs.map((r) => (r.id === data.id ? { ...r, ...data } : r)));
      toast.success("MMS final price saved");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSavingOverride(false); }
  };

  return (
    <div data-testid="mms-quote-dashboard-page">
      <PageHeader
        eyebrow="MMS Studio"
        title="MMS Quote Requests"
        description="Every simulation clients have submitted for an official quote. Preview the reference photo, review the estimate breakdown and mark each request as reviewed, converted or rejected."
        action={
          <button
            onClick={load}
            className="btn-ghost inline-flex items-center gap-2 text-xs"
            data-testid="mms-dash-refresh"
          >
            <RefreshCcw size={12} /> Refresh
          </button>
        }
      />

      {err && <div className="mb-6 border border-red-600/40 bg-red-50 text-red-700 text-xs p-3">{err}</div>}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="mms-dash-filters">
        {FILTERS.map((f) => {
          const count = f.statuses ? f.statuses.reduce((n, k) => n + (counts[k] || 0), 0) : counts.total;
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              data-testid={`mms-dash-filter-${f.key}`}
              className={`text-xs uppercase tracking-widest px-3 py-2 border inline-flex items-center gap-2 transition-colors ${
                active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
              }`}
            >
              <span>{f.label}</span>
              <span className={`text-[10px] tabular-nums ${active ? "text-white/70" : "text-[#737373]"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-[#737373]">
          <Loader2 size={14} className="animate-spin" /> Loading quote requests…
        </div>
      ) : filtered.length === 0 ? (
        <Empty title="Nothing to review" hint="No simulations match the current filter." />
      ) : (
        <div className="card-dark overflow-hidden" data-testid="mms-dash-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5]">
                <th className="py-3 px-4 w-16">Photo</th>
                <th className="py-3 px-4">Simulation</th>
                <th className="py-3 px-4">Brand</th>
                <th className="py-3 px-4">Client</th>
                <th className="py-3 px-4">Requested</th>
                <th className="py-3 px-4 text-right">Estimated total</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA] transition-colors"
                  data-testid={`mms-dash-row-${s.id}`}
                >
                  <td className="py-3 px-4"><Thumb sim={s} size={40} /></td>
                  <td className="py-3 px-4">
                    <div className="text-[#0A0A0A] truncate max-w-[240px]" title={s.name}>{s.name || "Untitled"}</div>
                    {(s.inputs?.garment?.subcategory || s.inputs?.garment?.primary_category || s.inputs?.garment?.category) && (
                      <div className="text-[10px] text-[#737373] uppercase tracking-widest mt-0.5">
                        {s.inputs?.garment?.subcategory || s.inputs?.garment?.primary_category || s.inputs?.garment?.category}
                        {s.inputs?.production?.quantity ? ` · ${s.inputs.production.quantity} pcs` : ""}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-[#0A0A0A]">{s.brand_name || "—"}</td>
                  <td className="py-3 px-4 text-xs">
                    <div className="text-[#0A0A0A]">{s.owner_name || "—"}</div>
                    <div className="text-[10px] text-[#737373]">{s.owner_email || ""}</div>
                  </td>
                  <td className="py-3 px-4 text-xs text-[#525252]">
                    {s.quote_requested_at ? new Date(s.quote_requested_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-3 px-4 text-sm tabular-nums text-[#0A0A0A] text-right">
                    {s.admin_final_price != null
                      ? <span title="MMS final price">{formatCurrency(s.admin_final_price, s.currency || "EUR")}</span>
                      : totalDisplay(s.estimate, s.currency || "EUR")}
                  </td>
                  <td className="py-3 px-4"><StatusPill status={s.status} /></td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => openDetail(s)}
                      className="btn-ghost inline-flex items-center gap-1 text-xs py-1 px-2"
                      data-testid={`mms-dash-open-${s.id}`}
                    >
                      <Eye size={12} /> Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.name || "Simulation"} wide>
        {detail && <DetailBody
          detail={detail}
          adminNote={adminNote} setAdminNote={setAdminNote}
          savingStatus={savingStatus} onChangeStatus={changeStatus}
          overrideDraft={overrideDraft} setOverrideDraft={setOverrideDraft}
          savingOverride={savingOverride} onSaveOverride={saveOverride}
        />}
      </Modal>
    </div>
  );
}

function DetailBody({ detail, adminNote, setAdminNote, savingStatus, onChangeStatus, overrideDraft, setOverrideDraft, savingOverride, onSaveOverride }) {
  const est = detail.estimate || {};
  const currency = detail.currency || "EUR";
  const ai = detail.inputs?.ai_analysis || null;
  const thumb = ai?.image_thumbnail_b64;
  const hours = est.hours || {};
  const aiTotal = aiEngineTotal(est);
  const overrideHistory = detail.admin_override_history || [];
  const finalPrice = detail.admin_final_price;
  const delta = (finalPrice != null) ? (Number(finalPrice) - aiTotal) : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="mms-dash-detail">
      <div className="md:col-span-1">
        {thumb ? (
          <img
            src={`data:image/jpeg;base64,${thumb}`}
            alt="reference"
            className="w-full aspect-square object-cover border border-[#E5E5E5] mb-3"
            data-testid="mms-dash-detail-photo"
          />
        ) : (
          <div className="w-full aspect-square bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center text-[#B3B3B3] mb-3">
            <ImageIcon size={48} strokeWidth={1.5} />
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Brand</div>
        <div className="text-sm text-[#0A0A0A] mb-3">{detail.brand_name || "—"}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">Client</div>
        <div className="text-sm text-[#0A0A0A]">{detail.owner_name || "—"}</div>
        <div className="text-[11px] text-[#525252]">{detail.owner_email || ""}</div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-3">Status</div>
        <div className="mt-1"><StatusPill status={detail.status} /></div>
      </div>

      <div className="md:col-span-2 space-y-6">
        {/* AI vs MMS pricing summary */}
        <div className="border border-[#0A0A0A] p-4 bg-[#FAFAFA]" data-testid="mms-dash-price-summary">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">AI engine total</div>
              <div className="font-serif text-lg tabular-nums text-[#0A0A0A]" data-testid="mms-dash-ai-total">{formatCurrency(aiTotal, currency)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">MMS final price</div>
              <div className="font-serif text-lg tabular-nums text-[#0A0A0A]" data-testid="mms-dash-final-price">
                {finalPrice != null ? formatCurrency(finalPrice, currency) : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-1">Δ vs AI</div>
              <div className={`font-serif text-lg tabular-nums ${delta == null ? "text-[#737373]" : delta === 0 ? "text-[#0A0A0A]" : delta > 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="mms-dash-delta">
                {delta == null ? "—" : `${delta > 0 ? "+" : ""}${formatCurrency(delta, currency)}`}
              </div>
            </div>
          </div>
        </div>

        {/* Internal AI breakdown (hours, rate, multipliers) — ADMIN ONLY */}
        <div>
          <div className="label-caps mb-2">Internal AI breakdown</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <EstRow k="Pattern (cartamodello)" v={est.pattern_cost ?? est.product_development} c={currency} />
            <EstRow k="Sample (campione)" v={est.sample_cost} c={currency} />
            <EstRow k="Materials (max)" v={est.materials?.max ?? est.materials} c={currency} />
            <EstRow k="Production labour" v={est.production} c={currency} />
            <EstRow k="Accessories (max)" v={est.accessories?.max ?? 0} c={currency} />
            <EstRow k="Processing (max)" v={est.processing?.max ?? est.customizations ?? 0} c={currency} />
          </div>
          <div className="border-t border-[#0A0A0A] mt-3 pt-3 flex justify-between items-baseline">
            <div className="label-caps">Engine total (AI)</div>
            <div className="font-serif text-xl tabular-nums" data-testid="mms-dash-detail-total">
              {totalDisplay(est, currency)}
            </div>
          </div>
          {est.quantity > 1 && (
            <div className="flex justify-between text-[11px] text-[#525252] mt-1">
              <span>Per unit labour</span>
              <span>{formatCurrency(est.production_per_unit || 0, currency)}</span>
            </div>
          )}
          {(hours.pattern != null || hours.sample != null) && (
            <div className="mt-3 pt-3 border-t border-[#E5E5E5] grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] text-[#737373] uppercase tracking-widest" data-testid="mms-dash-hours-grid">
              <div>Pattern hours <span className="text-[#0A0A0A] tabular-nums block text-sm">{hours.pattern ?? "—"}h</span></div>
              <div>Sample hours <span className="text-[#0A0A0A] tabular-nums block text-sm">{hours.sample ?? "—"}h</span></div>
              <div>Unit hours <span className="text-[#0A0A0A] tabular-nums block text-sm">{hours.production_unit ?? "—"}h</span></div>
              <div>Hourly rate <span className="text-[#0A0A0A] tabular-nums block text-sm">{formatCurrency(est.hourly_rate || 0, currency)}</span></div>
              {hours.material_handling_multiplier != null && (
                <div>Fabric mult. <span className="text-[#0A0A0A] tabular-nums block text-sm">×{hours.material_handling_multiplier}</span></div>
              )}
              {hours.quantity_multiplier != null && (
                <div>Qty mult. <span className="text-[#0A0A0A] tabular-nums block text-sm">×{hours.quantity_multiplier}</span></div>
              )}
              {hours.manual_work != null && Number(hours.manual_work) > 0 && (
                <div>Manual work <span className="text-[#0A0A0A] tabular-nums block text-sm">{hours.manual_work}h</span></div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="label-caps mb-2">Garment inputs</div>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
            <Info k="Category" v={detail.inputs?.garment?.primary_category || detail.inputs?.garment?.category} />
            <Info k="Subcategory" v={detail.inputs?.garment?.subcategory} />
            <Info k="Fit" v={detail.inputs?.garment?.fit} />
            <Info k="Complexity" v={detail.inputs?.garment?.complexity || detail.inputs?.production?.complexity} />
            <Info k="Quantity" v={detail.inputs?.production?.quantity} />
            <Info k="Pattern source" v={detail.inputs?.pattern?.source} />
            <Info k="Material family" v={detail.inputs?.materials?.handling_key} />
            <Info k="Multiple materials" v={detail.inputs?.materials?.multiple_materials ? "yes" : "no"} />
          </dl>
        </div>

        <div>
          <div className="label-caps mb-2">Construction modifiers</div>
          {(detail.inputs?.construction?.modifiers || []).length === 0
            ? <div className="text-[11px] text-[#737373] italic">None</div>
            : <div className="flex flex-wrap gap-2">
                {(detail.inputs.construction.modifiers || []).map((m, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#E5E5E5]">
                    {m.key}{m.count > 1 ? ` ×${m.count}` : ""}
                  </span>
                ))}
              </div>
          }
        </div>

        {(detail.inputs?.processing?.length > 0 || detail.inputs?.manual_work?.length > 0) && (
          <div>
            <div className="label-caps mb-2">Processing & manual work</div>
            <div className="flex flex-wrap gap-2">
              {(detail.inputs?.processing || []).map((p, i) => <span key={`p${i}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#E5E5E5]">{p.key}</span>)}
              {(detail.inputs?.manual_work || []).map((m, i) => <span key={`m${i}`} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#E5E5E5]">{m.key}</span>)}
            </div>
          </div>
        )}

        {detail.inputs?.notes && (
          <div>
            <div className="label-caps mb-2">Client notes</div>
            <div className="text-xs text-[#525252] whitespace-pre-wrap border border-[#E5E5E5] p-3">{detail.inputs.notes}</div>
          </div>
        )}

        {ai?.notes && (
          <div>
            <div className="label-caps mb-2">AI reasoning (internal)</div>
            <div className="text-xs text-[#525252] whitespace-pre-wrap border border-dashed border-[#E5E5E5] p-3" data-testid="mms-dash-ai-reasoning">{ai.notes}</div>
          </div>
        )}

        {/* Manual price override — logs delta history */}
        <div className="border border-[#0A0A0A] p-4" data-testid="mms-dash-override-block">
          <div className="label-caps mb-2">MMS final price (override)</div>
          <div className="text-[11px] text-[#525252] mb-3">
            Set the commercial price MMS Studio confirms to the client. Every edit is logged with the delta vs
            the AI engine total.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number" step="0.01" min="0"
              className="input-dark w-48"
              value={overrideDraft}
              onChange={(e) => setOverrideDraft(e.target.value)}
              placeholder="e.g. 450"
              data-testid="mms-dash-override-input"
            />
            <button
              onClick={onSaveOverride}
              disabled={savingOverride}
              className="btn-gold inline-flex items-center gap-2 text-xs disabled:opacity-50"
              data-testid="mms-dash-override-save"
            >
              {savingOverride ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Save MMS price
            </button>
          </div>
          {overrideHistory.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">Override history</div>
              <ul className="space-y-1" data-testid="mms-dash-override-history">
                {overrideHistory.slice().reverse().map((h, i) => (
                  <li key={i} className="text-[11px] text-[#525252] flex flex-wrap items-baseline gap-2">
                    <span className="tabular-nums text-[#0A0A0A]">{formatCurrency(h.final_price || 0, currency)}</span>
                    <span className="text-[10px]">
                      Δ {h.delta_vs_ai > 0 ? "+" : ""}{formatCurrency(h.delta_vs_ai || 0, currency)} vs AI
                    </span>
                    <span className="text-[10px] text-[#737373]">
                      {h.at ? new Date(h.at).toLocaleString() : ""}
                    </span>
                    {h.note && <span className="text-[10px] italic">— {h.note}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div>
          <div className="label-caps mb-2">Admin note</div>
          <textarea
            className="input-dark w-full text-xs"
            rows={3}
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Internal note visible only to MMS Studio admins…"
            data-testid="mms-dash-detail-note"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E5E5E5]">
          <button
            disabled={savingStatus}
            onClick={() => onChangeStatus("in_review")}
            className="btn-ghost inline-flex items-center gap-2 text-xs disabled:opacity-50"
            data-testid="mms-dash-detail-inreview"
          >
            <Eye size={12} /> Mark In review
          </button>
          <button
            disabled={savingStatus}
            onClick={() => onChangeStatus("converted")}
            className="btn-gold inline-flex items-center gap-2 text-xs disabled:opacity-50"
            data-testid="mms-dash-detail-converted"
          >
            <Check size={12} /> Mark Converted
          </button>
          <button
            disabled={savingStatus}
            onClick={() => onChangeStatus("rejected")}
            className="text-xs uppercase tracking-widest px-3 py-2 border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="mms-dash-detail-rejected"
          >
            <XCircle size={12} /> Reject
          </button>
          {savingStatus && <Loader2 size={14} className="animate-spin text-[#737373]" />}
        </div>
      </div>
    </div>
  );
}

function EstRow({ k, v, c }) {
  return (
    <div className="flex justify-between border-b border-[#F0F0F0] py-1">
      <span className="text-[#525252]">{k}</span>
      <span className="tabular-nums text-[#0A0A0A]">{formatCurrency(v || 0, c)}</span>
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#737373]">{k}</dt>
      <dd className="text-[#0A0A0A]">{v !== undefined && v !== null && v !== "" ? String(v) : "—"}</dd>
    </div>
  );
}
